import { FEATURE_FLAGS } from '../constant/featureFlags';
import {
  hasDisbursementsEnabled,
  hasResidualsEnabled,
} from '../utils/helpers/common';
import { useActiveWorkspace } from './useActiveWorkspace';
import { useCanAccessRoyalties } from './useCanAccessRoyalties';
import { useFeatureFlag } from './useFeatureFlag';

/**
 * The addon check behind each statements page. `PaymentsRedirect` reads these
 * with the workspace it has already loaded, so its fallback never picks a page
 * the nav hides; the route guards in `WorkspaceRoutes` use the same two
 * functions from `utils/helpers/common`.
 */
export const STATEMENT_ADDON_CHECKS = {
  disbursements: hasDisbursementsEnabled,
  residuals: hasResidualsEnabled,
};

/**
 * Single source of truth for which MSR3 royalties links exist and how the
 * Statements group is labelled and ordered.
 *
 * Each `show*` flag combines the route flag with the access check for that
 * page, so nav never offers a link the route guard would answer with
 * not-found. `Sidebar` and `RoyaltiesLayout` both read this, which is why the
 * access half lives here rather than in either component — a pure flag OR let
 * the group rename itself to "Earnings Statements" with no royalties link
 * above it, leaving a lone "Historic Royalties" and no current-royalties page.
 *
 * The membership fetch is shared (react-query cache), so extra callers cost
 * nothing on the wire.
 */
export const useRoyaltiesRouteFlags = () => {
  const [activeWorkspace] = useActiveWorkspace();
  const [isPaymentsEnabled] = useFeatureFlag(FEATURE_FLAGS.MSR3_PAYMENTS_ROUTE);
  const [isRoyaltiesAnzEnabled] = useFeatureFlag(
    FEATURE_FLAGS.MSR3_ROYALTIES_ANZ_ROUTE,
  );
  const [isRoyaltiesWorldEnabled] = useFeatureFlag(
    FEATURE_FLAGS.MSR3_ROYALTIES_WORLD_ROUTE,
  );

  // Skip the membership fetch while payments MSR3 is off; access flags stay
  // false until it settles so links do not flash on a stale membershipStatus.
  const {
    canAccessRoyalties,
    canAccessRoyaltiesAnz,
    canAccessRoyaltiesWorld,
    isLoadingMembership,
  } = useCanAccessRoyalties(activeWorkspace, isPaymentsEnabled);

  const showRoyaltiesAnzNav = isRoyaltiesAnzEnabled && canAccessRoyaltiesAnz;
  const showRoyaltiesWorldNav =
    isRoyaltiesWorldEnabled && canAccessRoyaltiesWorld;

  // While membership resolves every show* flag is false, so deciding on access
  // alone would render "Statements" and then flip to "Earnings Statements" on
  // every load for members who do hold the addon. Fall back to the route flags
  // for that window: no royalties link renders either way, and the only
  // configuration that briefly reads wrong is the one that settles to
  // "Statements" a moment later.
  const isEarningsStatementsGroup = isLoadingMembership
    ? isRoyaltiesAnzEnabled || isRoyaltiesWorldEnabled
    : showRoyaltiesAnzNav || showRoyaltiesWorldNav;

  return {
    /**
     * True once a region link actually renders above Historic Royalties (or,
     * while membership resolves, once its route flag is on). Drives the
     * "Earnings Statements" group label, the "Historic Royalties" rename, and
     * moving Historic below Disbursements/Residuals.
     */
    isEarningsStatementsGroup,
    /** True while access is still resolving — hold labels that would flip. */
    isLoadingMembership,
    showDisbursementsNav: STATEMENT_ADDON_CHECKS.disbursements(activeWorkspace),
    showResidualsNav: STATEMENT_ADDON_CHECKS.residuals(activeWorkspace),
    showRoyaltiesAnzNav,
    /** Historic Royalties (MSR2) — any royalties addon. */
    showRoyaltiesNav: canAccessRoyalties,
    showRoyaltiesWorldNav,
  };
};
